//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import HookMaskedInput from '@/components/UI/HookForm/Form/MaskedInput';
import { HookRadioGroup } from '@/components/UI/HookForm/Form/RadioGroup';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { type UploadFnReturn, useFileUpload } from '@/hooks/useFileUpload';
import { compareTwoArrays } from '@/utils';

//STP Libraries

import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
//Assets and Miscellaneous
import { States } from '../../../../../../constants';
import {
  acquisitionTypes,
  constructionTypeOptions,
  neighborhoods,
  projectByRightOptions,
  projectTypeOptions,
  siteControlStatuses,
} from './constants';

//Component Begin

export default function ProjectDetails() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    formState: { errors, isValid },
    watch,
    setFocus,
  } = useFormContext();

  const projectType = watch('projectDetails.projectType');
  const isPropertyCityOwn = watch('projectDetails.isPropertyCityOwn');
  const familyType = watch('projectDetails.familyType');

  const uploadFile = useMutation((payload: { signAssetPayload: ISignApplicationPublicAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: ({ sign, files }: UploadFnReturn) => {
      let signRes: SignedApplicationAssetResponse[] = [];
      if (Array.isArray(sign)) {
        signRes = [...sign];
      } else {
        signRes = [{ ...sign }];
      }

      updateData({
        projectDetails: {
          ...getValues('projectDetails'),
          additionalDocs: signRes.map((sig, index) => ({
            name: files[index].name,
            path: sig.path,
            key: sig.key,
            type: files[index].type,
            field: 'additionalDocs',
            description: 'Additional Docs',
          })),
        },
      });
      setCurrentStep((prev) => prev + 1);
    },
    onError: (_error: any) => {
      setUploadError('Failed to upload document');
    },
  });

  // useEffect(() => {
  //   if (!isObjectEmpty(errors)) {
  //     const firstErrorKey = Object.keys(errors?.projectDetails)[0];
  //     setFocus(`projectDetails.${firstErrorKey}`);
  //   }
  // }, [errors, setFocus]);

  const onSubmit = () => {
    trigger();
    if (!isValid) {
      return;
    }

    const formData = getValues('projectDetails');

    if (formData.selectedFiles?.length) {
      const formDataSelectedFiles = formData?.selectedFiles.map((f: File) => f.name);
      const formContextDataUploadedFiles = formContextData?.projectDetails?.additionalDocs.map((f: File) => f.name);

      const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

      if (fileUploadCompare.array1Diff.length > 0) {
        const signAssetPayload = formData.selectedFiles
          .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
          .map((file: File) => ({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            formId: String(params.applicationFormId),
          }));
        uploadFile.mutate({
          signAssetPayload,
          files: formData.selectedFiles,
        });
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    } else {
      updateData({
        projectDetails: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Proposed Project Details</h3>
        <p className='mt-1 text-sm text-gray-500'>Details about your proposed project</p>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <HookInput
          customClassName='col-span-6 sm:col-span-6 lg:col-span-2'
          label='Street Address'
          name='projectDetails.streetAddress'
          type='text'
        />
        <HookInput customClassName='col-span-6 sm:col-span-6 lg:col-span-2' label='City' name='projectDetails.city' type='text' />
        <HookSelect
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='State / Province'
          name='projectDetails.state'
          options={States.map((s) => ({
            label: s.name,
            value: s.name,
          }))}
        />
        <HookMaskedInput
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Zip / Postal Code'
          name='projectDetails.zipCode'
          maskedComponent='zipCode'
        />
        <HookSelect
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Neighborhood'
          name='projectDetails.neighborhood'
          options={neighborhoods.map((n) => ({ label: n, value: n }))}
        />
        <HookSelect
          customClassName='col-span-6 sm:col-span-2'
          label='Proposed Project Type'
          name='projectDetails.projectType'
          options={projectTypeOptions.map((d) => ({
            label: d,
            value: d,
          }))}
        />
        {projectType && projectType === 'Mixed Use' && (
          <HookMaskedInput
            customClassName='col-span-6 sm:col-span-2'
            label='No. of Commercial Sq.ft'
            name='projectDetails.noOfCommercial'
            id='noOfCommercial'
            maskedComponent='numeric'
          />
        )}
        {projectType && projectType === 'Affordable Homeownership' && (
          <HookMaskedInput
            customClassName='col-span-6 sm:col-span-2'
            label='Average Sales Price'
            name='averageSalesPrice'
            id='projectDetails.averageSalesPrice'
            maskedComponent='numeric'
          />
        )}
        <HookMaskedInput
          customClassName='col-span-6 sm:col-span-2'
          label='Proposed Number of Units'
          name='projectDetails.proposedUnits'
          id='proposedUnits'
          maskedComponent='numeric'
        />
        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Is Proposed Project By-Right'
          name='projectDetails.projectByRight'
          options={projectByRightOptions.map((v) => ({
            label: v,
            value: v,
          }))}
        />
        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Proposed Construction Type'
          name='projectDetails.constructionType'
          options={constructionTypeOptions.map((c) => ({
            label: c,
            value: c,
          }))}
        />
        {/* <HookMaskedInput
          customClassName="col-span-6 sm:col-span-3 lg:col-span-2"
          label="Proposed Purchase Price"
          name="projectDetails.proposedPurchasePrice"
          id="proposedPurchasePrice"
          maskedComponent="currency"
        /> */}
        <HookMaskedInput
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Estimated Development Cost'
          name='projectDetails.developmentCostEstimate'
          id='developmentCostEstimate'
          maskedComponent='currency'
        />
        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Proposed Acquisition Type'
          name='projectDetails.acquisitionType'
          options={acquisitionTypes.map((a) => ({
            label: a,
            value: a,
          }))}
        />
        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Proposed Status Of Site Control'
          name='projectDetails.siteControlStatus'
          options={siteControlStatuses.map((s) => ({
            label: s,
            value: s,
          }))}
        />
        <HookRadioGroup
          className='col-span-6 md:col-span-6'
          label='Do you have community support?'
          name='projectDetails.haveCommunitySupport'
          id='have-community-support'
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
            {
              label: 'Have not presented to community',
              value: 'haveNotPresented',
            },
          ]}
        />
        <HookRadioGroup
          className='col-span-6 md:col-span-6'
          label='Is the property city owned?'
          name='projectDetails.isPropertyCityOwn'
          id='is-property-city-own'
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
        {isPropertyCityOwn === 'yes' && (
          <HookMaskedInput
            customClassName='col-span-6 sm:col-span-3 lg:col-span-3'
            label='How much are you proposing to pay for the property?'
            name='projectDetails.proposedAmount'
            id='proposed-amount'
            maskedComponent='currency'
          />
        )}
        <HookRadioGroup
          className='col-span-6 md:col-span-6'
          label='Is this a Single-family or Multi-Family project?'
          name='projectDetails.familyType'
          id='family-type'
          options={[
            { label: 'Single Family', value: 'single' },
            { label: 'Multi-Family', value: 'multi' },
            { label: 'Not Applicable', value: 'not applicable' },
          ]}
        />
        {familyType === 'single' && (
          <div className='grid grid-cols-3 col-span-3 gap-3'>
            <label className='col-span-3'>What will be the average sales price?</label>
            <HookMaskedInput
              customClassName='col-span-1 sm:col-span-1 lg:col-span-1'
              name='projectDetails.avgSalesPrice'
              id='avg-sales-price'
              maskedComponent='currency'
            />
          </div>
        )}
        {familyType === 'multi' && (
          <>
            <label className='col-span-6 leading-7 block text-sm font-medium text-neutralColor-medium'>
              Please provide the rental for the different unit types
            </label>
            <HookMaskedInput
              customClassName='col-span-6 lg:col-span-1'
              label='Studios'
              name='projectDetails.rentType.studios'
              maskedComponent='currency'
            />
            <HookMaskedInput
              customClassName='col-span-6 lg:col-span-1'
              label='1 Bedroom'
              name='projectDetails.rentType.oneBedroom'
              maskedComponent='currency'
            />
            <HookMaskedInput
              customClassName='col-span-6 lg:col-span-1'
              label='2 Bedrooms'
              name='projectDetails.rentType.twoBedrooms'
              maskedComponent='currency'
            />
            <HookMaskedInput
              customClassName='col-span-6 lg:col-span-1'
              label='3 Bedrooms'
              name='projectDetails.rentType.threeBedrooms'
              maskedComponent='currency'
            />
          </>
        )}
        <div className='col-span-6'>
          <HookTextarea
            name='projectDetails.moreInfo'
            label='More Information About Project'
            placeholder='Please tell us more about this proposed project. Specifically highlight any timing sensitivity.'
            rows={5}
          />
        </div>
        <div className='col-span-6'>
          <HookFileInput
            name='projectDetails.selectedFiles'
            label='Additional Docs'
            id='selectedFiles'
            placeholder='Upload Additional Documents'
            multiple
          />
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button
          label='Previous'
          disabled={uploadFile.isLoading}
          btnStyle='btn-neutral'
          type='button'
          onClick={() => setCurrentStep((prev) => prev - 1)}
        />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
